import React, { useCallback, useEffect, useRef, useState } from "react";

const AUTO_EVERY_MS = 3000;
const PAUSE_AFTER_TOUCH_MS = 8000;

// A row of buttons (the header's categories, the home page's category pills) that scrolls sideways like a carousel:
// ‹ › arrows appear on a side only while there is more to see there, and swiping or a trackpad still works.
// With autoScroll it moves on by itself every few seconds (back to the start at the end), and holds still while the
// pointer is over it, while it has focus, for a while after a touch, and for people who ask for reduced motion.
export default function ScrollRow({ className, wrapClassName = "", label, autoScroll = false, children }) {
    const rowRef = useRef(null);
    const [edges, setEdges] = useState({ start: true, end: true });
    const pausedUntil = useRef(0);
    const hovering = useRef(false);

    const update = useCallback(() => {
        const row = rowRef.current;
        if (!row) return;
        const start = row.scrollLeft <= 4;
        const end = row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
        setEdges((current) => (current.start === start && current.end === end ? current : { start, end }));
    }, []);

    useEffect(() => {
        const row = rowRef.current;
        if (!row) return undefined;
        row.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        return () => {
            row.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [update]);
    // The buttons inside change (categories load, the active one changes): measure again
    useEffect(update);

    const scroll = (direction) => {
        const row = rowRef.current;
        if (row) row.scrollBy({ left: direction * row.clientWidth * 0.8, behavior: "smooth" });
    };
    const pause = () => { pausedUntil.current = Date.now() + PAUSE_AFTER_TOUCH_MS; };

    useEffect(() => {
        if (!autoScroll || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        const timer = setInterval(() => {
            const row = rowRef.current;
            if (!row || hovering.current || Date.now() < pausedUntil.current || row.contains(document.activeElement)) return;
            if (row.scrollWidth - row.clientWidth <= 4) return; // everything fits
            if (row.scrollLeft + row.clientWidth >= row.scrollWidth - 4) row.scrollTo({ left: 0, behavior: "smooth" });
            else row.scrollBy({ left: Math.min(240, row.clientWidth * 0.5), behavior: "smooth" });
        }, AUTO_EVERY_MS);
        return () => clearInterval(timer);
    }, [autoScroll]);

    return (
        <div
            className={`scrollRow ${wrapClassName} ${edges.start ? "" : "canPrev"} ${edges.end ? "" : "canNext"}`}
            onMouseEnter={() => { hovering.current = true; }}
            onMouseLeave={() => { hovering.current = false; }}
            onTouchStart={pause}
            onWheel={pause}
        >
            {!edges.start && <button type="button" className="scrollRowArrow isPrev" aria-label={`Scroll ${label} left`} onClick={() => { pause(); scroll(-1); }}>‹</button>}
            <div className={className} ref={rowRef} role="group" aria-label={label}>
                {children}
            </div>
            {!edges.end && <button type="button" className="scrollRowArrow isNext" aria-label={`Scroll ${label} right`} onClick={() => { pause(); scroll(1); }}>›</button>}
        </div>
    );
}
